import { isMembershipLine } from '@notifi/shared-types/membership-line';
import type {
  TaxBreakdownItem,
  TaxGroup,
  TaxLineType,
  TaxSnapshot,
  TaxSummaryBreakdown,
} from '@/types/tax';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatTaxRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

/** Customer-facing label for the jurisdiction tier stored in a tax snapshot. */
export function formatTaxJurisdictionType(jurisdictionType?: string | null): string | null {
  const normalized = jurisdictionType?.trim();
  if (!normalized) return null;

  return normalized.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

export function parseAddressParts(address: string): {
  city?: string;
  state?: string;
  zip?: string;
} {
  const zip = address.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1];
  const stateMatch = address.match(/\b([A-Z]{2})\b/);
  const state = stateMatch?.[1];

  const parts = address.split(',').map((s) => s.trim());
  let city: string | undefined;
  if (parts.length >= 2) {
    city = parts[parts.length - 2];
  }

  return { city, state, zip };
}

export function normalize(s: string): string {
  return s.toLowerCase().trim();
}

// ─── Line type derivation ─────────────────────────────────────────────────────

/**
 * The tax classification of ONE line, derived from what the line IS.
 *
 * Mirrors `resolveLineTaxLineType` in operations' pricing engine — if the two
 * disagree the operator is shown tax the server will not charge.
 *
 * A service is labor and a part stated on its own line is materials, and a
 * stored `taxLineType` does not get to say otherwise: a MATERIAL carrying
 * `labor` escaped every materials-only code in the jurisdiction, and a SERVICE
 * carrying `materials` was taxed in states that exempt labor. `exempt` and
 * `freight` stay the operator's, because no line type expresses them.
 *
 * `labor` is a CLASSIFICATION, not an exemption — each jurisdiction's codes
 * decide what labor costs.
 */
export function resolveLineTaxLineType(line: {
  type?: string | null;
  taxable?: boolean;
  taxLineType?: TaxLineType | string | null;
}): TaxLineType {
  if (line.taxable === false) return 'exempt';

  const stated = String(line.taxLineType ?? '')
    .trim()
    .toLowerCase();
  if (stated === 'exempt') return 'exempt';
  if (stated === 'freight') return 'freight';

  switch (
    String(line.type ?? '')
      .trim()
      .toUpperCase()
  ) {
    case 'MATERIAL':
    case 'EQUIPMENT':
      return 'materials';
    case 'MEMBERSHIP':
      return 'exempt';
    // SERVICE, BUNDLE and anything unrecognised sell work, and so does a FEE —
    // a trip or dispatch charge buys the visit the work happens on, and the
    // jurisdiction answers for it with the same `service.taxable` that puts
    // `labor` in a code's `appliesTo`. `flat_rate` already matched exactly the
    // codes `labor` matches (no code can apply to `flat_rate` alone), so this
    // names the behaviour rather than changing it.
    default:
      return 'labor';
  }
}

// ─── Group tax calculation ────────────────────────────────────────────────────

/**
 * Which tax-group item produced a breakdown row.
 *
 * Kept OFF the row on purpose: rows are frozen into snapshots and sent to the
 * server, and a group-item reference is neither. Document-level rounding needs
 * it because a code is rounded once per DOCUMENT, and "the same code" means the
 * same group item — two distinct 10% codes that print identically are still two
 * totals ($2.51 + $2.51 = $5.02 over three $8.35 lines, not one $5.01).
 */
const breakdownSource = new WeakMap<object, object>();

/** The tax-group item a preview breakdown row came from, when known. */
export function taxBreakdownSourceItem(entry: object): object | undefined {
  return breakdownSource.get(entry);
}

function calculateGroupTax(
  lineSubtotal: number,
  lineType: TaxLineType,
  groupItems: TaxGroup['items'] = [],
): { totalTax: number; breakdown: TaxBreakdownItem[] } {
  // A base of ZERO still reaches the codes: a fixed-amount code is owed
  // whatever the base is, and the engine's `calculateGroupTax` has never
  // guarded against one — a bundle discounted to $0 was charged its fee there
  // and shown $0 here. Only a bundle parent gets this far on a zero base;
  // `computeTaxOnNet` still turns every other zero-net line away, so a part
  // priced at $0 by the double-charging rule cannot pick up a fee of its own.
  // A NEGATIVE base is not a sale, and is still refused.
  if (lineType === 'exempt' || lineSubtotal < 0) {
    return { totalTax: 0, breakdown: [] };
  }

  let runningTotal = lineSubtotal;
  let totalTax = 0;
  const breakdown: TaxBreakdownItem[] = [];
  const sorted = [...groupItems].sort((a, b) => a.sortOrder - b.sortOrder);

  for (const item of sorted) {
    const taxCode = item.taxCode;
    if (!taxCode) continue;

    // `appliesTo` is a multi-value set (Prisma `String[]`), e.g. `['all']` or
    // `['materials','labor']`. A scalar `!== 'all'` check never matched an array,
    // silently zeroing every taxable line. Mirror the backend `matchesLineCategory`
    // so the FE preview equals the persisted total.
    const appliesToSet = Array.isArray(taxCode.appliesTo)
      ? taxCode.appliesTo
      : taxCode.appliesTo
        ? [taxCode.appliesTo]
        : [];
    const appliesToLine =
      appliesToSet.includes('all') ||
      appliesToSet.includes(lineType) ||
      (lineType === 'flat_rate' && appliesToSet.includes('labor'));
    if (!appliesToLine) {
      continue;
    }

    const base = item.isCompound ? runningTotal : lineSubtotal;
    const amount = taxCode.rateType === 'fixed' ? taxCode.rate : base * taxCode.rate;

    if (amount <= 0) continue;

    totalTax += amount;
    runningTotal += amount;
    // Server parity (`buildTaxSnapshot`): a rule override kept off documents
    // still charges, but prints as a nameless "Tax" row. At 0% it never gets
    // here — a zero amount is skipped above.
    const hidden = taxCode.hideOnDocument === true;
    const row: TaxBreakdownItem = {
      // v60 carries `name` (e.g. "US State Sales Tax", "GST (Federal)") and
      // `jurisdiction` (e.g. "NJ", "BERGEN") as distinct fields. We surface
      // both so the customer-facing breakdown can render the primary label
      // (`name`) with the optional jurisdiction qualifier on the side.
      name: hidden ? null : (taxCode.name ?? null),
      jurisdiction: hidden ? null : (taxCode.jurisdiction ?? null),
      jurisdictionType: hidden ? null : (taxCode.jurisdictionType ?? null),
      rate: hidden ? 0 : taxCode.rate,
      ...(hidden ? { hidden: true } : {}),
      appliesTo: taxCode.appliesTo,
      amount,
      isCompound: item.isCompound,
      registrationNumber: hidden ? null : (taxCode.registrationNumber ?? null),
    };
    breakdownSource.set(row, item);
    breakdown.push(row);
  }

  return { totalTax, breakdown };
}

// ─── Line item tax preview ────────────────────────────────────────────────────

export interface PickedLineItemLike {
  type: string;
  quantity: number;
  basePrice: number;
  taxable?: boolean;
  priceStrategy?: string;
  taxLineType?: TaxLineType | null;
  laborRate?: number;
  /**
   * Where the line's money posts. `'membership'` makes the line a MEMBERSHIP
   * whatever its type says, and a membership is exempt — see the guard below.
   */
  taxCategory?: string | null;
}

export interface LocalTaxPreview {
  taxAmount: number;
  taxSnapshot: TaxSnapshot | null;
}

export function calculateLineItemTaxPreview(
  lineItem: PickedLineItemLike,
  taxGroup: TaxGroup | null,
  resolvedFrom: TaxSnapshot['resolvedFrom'] = null,
  /**
   * The classification a BUNDLE PARENT carries for the whole base, decided by
   * what is in the bundle (`resolveBundleTaxLineType`). Only the bundle path
   * passes it; every other line is classified from what it IS.
   */
  bundleTaxLineType?: TaxLineType,
): LocalTaxPreview {
  const quantity = lineItem.quantity ?? 1;
  const subtotal = (lineItem.basePrice ?? 0) * quantity;
  // Exempt wins over the bundle, and is checked FIRST — lockstep with
  // `calculateLineTaxV2`, which short-circuits on the line's own classification
  // before it ever applies the bundle treatment. Letting the bundle overrule it
  // took the operator's one escape hatch away from bundled sales, and did it
  // only in the preview: the server would still have charged zero.
  const ownTaxLineType = resolveLineTaxLineType(lineItem);
  const taxLineType =
    ownTaxLineType === 'exempt' ? 'exempt' : (bundleTaxLineType ?? ownTaxLineType);
  const taxable = lineItem.taxable ?? true;

  // A MEMBERSHIP is exempt, and it says so by TYPE or by `taxCategory` — the
  // same one predicate operations charges by (`owesNoTax` ->
  // `isMembershipLine`). `resolveLineTaxLineType` above reads only the type,
  // deliberately: it is a classification table keyed on what a line IS, and
  // `taxCategory` is an accounting field rather than a type. So the membership
  // question is asked HERE, on top of it, exactly as the engine asks it.
  //
  // Without this, a service-shaped membership — `type: 'SERVICE'`,
  // `taxCategory: 'membership'` — was taxed $50.00 on a $1,000 plan in the
  // preview and saved at $0.00 by the server. Same shape as the bundle-member
  // gap, one case over: standalone instead of inside a bundle.
  if (!taxGroup || !taxable || taxLineType === 'exempt' || isMembershipLine(lineItem)) {
    return {
      taxAmount: 0,
      taxSnapshot: {
        groupId: taxGroup?.id ?? null,
        groupName: taxGroup?.name ?? null,
        combinedRate: taxGroup?.combinedRate ?? 0,
        resolvedFrom: resolvedFrom,
        breakdown: [],
        laborTax: null,
        materialTax: null,
      },
    };
  }

  // ONE line, ONE classification. A dynamically-priced service is not split
  // into a labor slice and a materials slice: the ratio came from the
  // service's own rate fields, which the customer's document never states, so
  // it taxed dollars as goods that were never shown as goods. Lockstep with
  // `calculateLineTaxV2` in operations. Materials that must be taxed as
  // materials belong on a line of their own.
  const result = calculateGroupTax(subtotal, taxLineType, taxGroup.items);

  return {
    taxAmount: result.totalTax,
    taxSnapshot: {
      groupId: taxGroup.id,
      groupName: taxGroup.name,
      combinedRate: taxGroup.combinedRate,
      resolvedFrom: resolvedFrom,
      breakdown: result.breakdown,
      laborTax: null,
      materialTax: null,
    },
  };
}

// ─── Named-jurisdiction labels (v60) ──────────────────────────────────────────

/**
 * Tokenize a label for substring-aware comparison. We split on whitespace
 * and commas, lowercase, and drop empties. The output is a sequence of
 * tokens (not a set) so we can detect both word overlap and contiguous
 * prefix subsumption inside `dedupLabelTokens`.
 */
function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Remove tokens from `secondary` that already appear in `primary`. This
 * collapses redundant context like:
 *
 *   "ALDEN MANOR City Tax" / "ALDEN MANOR, NASSAU County"
 *     → "ALDEN MANOR City Tax" / "NASSAU County"
 *   "NY State Tax" / "NY State"           → "NY State Tax" / null
 *   "NY NASSAU County" / "NASSAU County"  → "NY NASSAU County" / null
 *
 * If every token of `secondary` is already in `primary` we return null
 * (no secondary line). Otherwise we reconstruct the secondary from the
 * remaining tokens of the ORIGINAL string so casing + ordering survive.
 * Tokens like "City", "County", "State" are treated as content; they
 * carry the tier qualifier (Brian wants "NASSAU County" not just
 * "NASSAU"). We don't try to be clever about morphological variants —
 * this is a presentation fix, not a search engine.
 */
function dedupSecondaryAgainstPrimary(primary: string, secondary: string): string | null {
  const primaryTokens = new Set(tokenize(primary));
  if (primaryTokens.size === 0) return secondary;

  // Walk the original secondary token-by-token. We split on the same
  // separator class (whitespace + commas) and remember the separator we
  // ate so the rejoin doesn't crunch "NASSAU, County" into
  // "NASSAU County".
  const pieces = secondary.split(/([\s,]+)/);
  const kept: string[] = [];
  let pendingSep: string | null = null;
  let anyKept = false;
  for (let i = 0; i < pieces.length; i += 1) {
    const piece = pieces[i] ?? '';
    if (i % 2 === 1) {
      // Odd index = separator chunk from the split above. We hold onto
      // it until we know the next token survives; otherwise it'd leave
      // dangling commas like " , County".
      pendingSep = piece;
      continue;
    }
    if (!piece) continue;
    const norm = piece.toLowerCase();
    if (primaryTokens.has(norm)) {
      // Drop this token AND the separator that led into it — otherwise
      // we'd leave double-spaces or leading punctuation.
      pendingSep = null;
      continue;
    }
    if (anyKept && pendingSep) {
      kept.push(pendingSep);
    }
    kept.push(piece);
    pendingSep = null;
    anyKept = true;
  }
  const rejoined = kept
    .join('')
    .replace(/^[,\s]+|[,\s]+$/g, '')
    .trim();
  return rejoined.length === 0 ? null : rejoined;
}

/**
 * Compute the customer-facing primary + secondary label for one
 * `TaxBreakdownItem`. v60 fetches populate `name` from the provider's
 * `jurDescription` ("US State Sales Tax", "GST (Federal)") and
 * `jurisdiction` from `jurName` ("NJ", "BERGEN", "LYNDHURST").
 *
 * Rules (mirror EstimatePDF / InvoicePDF / TaxZipLookupDialog from PR #591):
 * - Primary  = `name` if non-empty, otherwise `jurisdiction`, else "Tax".
 * - Secondary = `jurisdiction` when it adds information (i.e. distinct from
 *   the primary). Canada's pre-formatted names ("GST (Federal)",
 *   "PST - British Columbia") already encode the locale, so the secondary
 *   collapses to null and the breakdown row renders as a single line.
 *
 * Tenant pencil flows (NOT- city-tier) produce label pairs where the
 * jurisdiction already contains the primary's leading words (e.g.
 * primary "ALDEN MANOR City Tax" + secondary "ALDEN MANOR, NASSAU
 * County"). We strip any secondary tokens already present in the
 * primary so the row reads "ALDEN MANOR City Tax — NASSAU County"
 * instead of "ALDEN MANOR City Tax — ALDEN MANOR, NASSAU County".
 */
export function taxBreakdownLabel(item: { name?: string | null; jurisdiction?: string | null }): {
  primary: string;
  secondary: string | null;
} {
  const name = (item.name ?? '').trim();
  const jurisdiction = (item.jurisdiction ?? '').trim();
  if (name && jurisdiction && name !== jurisdiction) {
    const deduped = dedupSecondaryAgainstPrimary(name, jurisdiction);
    return { primary: name, secondary: deduped };
  }
  return { primary: name || jurisdiction || 'Tax', secondary: null };
}

/** Aggregated tax-code row for the customer-facing summary panels. */
export interface AggregatedTaxBreakdownRow {
  primary: string;
  secondary: string | null;
  rate: number;
  amount: number;
  jurisdictionType: string | null;
  registrationNumber: string | null;
  /**
   * Every rule override kept off documents, folded together: it prints as
   * plain "Tax" with no tier or rate, since none of theirs is the row's.
   */
  hidden?: boolean;
}

/** Join the tax-code name and its distinct jurisdiction into one display label. */
export function taxBreakdownDisplayName(
  row: Pick<AggregatedTaxBreakdownRow, 'primary' | 'secondary'>,
): string {
  return row.secondary ? `${row.primary} — ${row.secondary}` : row.primary;
}

/** Render the jurisdiction tier and frozen percentage stored with a tax row. */
export function taxBreakdownDisplayMeta(
  row: Pick<AggregatedTaxBreakdownRow, 'jurisdictionType' | 'rate' | 'hidden'>,
): string {
  if (row.hidden) return '';
  const tier = formatTaxJurisdictionType(row.jurisdictionType);
  return [tier, formatTaxRate(row.rate)].filter(Boolean).join(' · ');
}

/** "Name · tier · rate", or just the name when the row carries no meta. */
export function taxBreakdownDisplayLabel(row: AggregatedTaxBreakdownRow): string {
  const meta = taxBreakdownDisplayMeta(row);
  return meta ? `${taxBreakdownDisplayName(row)} · ${meta}` : taxBreakdownDisplayName(row);
}

/**
 * Fold the per-line `taxSnapshot.breakdown` arrays into one row per tax
 * code so summary blocks can render "GST (Federal) — 5%" + "PST -
 * British Columbia — 7%" as separate lines instead of a rolled-up
 * "Tax 12%".
 *
 * Grouping key = `name|jurisdiction` so:
 *   - US codes with the same name but different jurisdictions
 *     (NJ State Sales Tax vs. BERGEN County Sales Tax) stay separate.
 *   - Repeat applications of the same code across multiple line items
 *     fold into a single row with summed amount.
 *
 * Returns an empty array when no line has a snapshot — the caller falls
 * back to the rolled-up `Tax` total in that case.
 */
export function aggregateTaxBreakdown(
  lineItems: Array<{ taxSnapshot?: TaxSnapshot | Record<string, unknown> | null }>,
): AggregatedTaxBreakdownRow[] {
  const byCode = new Map<string, AggregatedTaxBreakdownRow>();
  for (const li of lineItems) {
    const snap = li.taxSnapshot as TaxSnapshot | null | undefined;
    const entries = (snap?.breakdown ?? []) as TaxBreakdownItem[];
    for (const entry of entries) {
      const { primary, secondary } = taxBreakdownLabel(entry);
      // Name + jurisdiction alone are not always unique. Preserve separately
      // applicable tiers/rates (for example a state and district code sharing
      // a tenant-authored label) so customer documents list every tax applied.
      // Hidden rule overrides fold into ONE plain "Tax" row whatever their
      // rates: the customer is shown the money, not how it was made up.
      const key = entry.hidden
        ? 'hidden'
        : [primary, secondary ?? '', entry.jurisdictionType ?? '', entry.rate].join('|');
      const existing = byCode.get(key);
      if (existing) {
        existing.amount += entry.amount;
        if (!existing.registrationNumber && entry.registrationNumber) {
          existing.registrationNumber = entry.registrationNumber;
        }
      } else {
        byCode.set(key, {
          primary,
          secondary,
          rate: entry.rate,
          amount: entry.amount,
          jurisdictionType: entry.jurisdictionType ?? null,
          registrationNumber: entry.registrationNumber ?? null,
          ...(entry.hidden ? { hidden: true } : {}),
        });
      }
    }
  }
  return Array.from(byCode.values());
}

/**
 * Return a customer-displayable breakdown only when it accounts for the full
 * stored tax amount. Historical estimates may have snapshots on only some
 * lines; showing those rows alone would imply that the omitted tax did not
 * apply. In that case renderers must fall back to their rolled-up Tax row.
 */
export function reconciledTaxBreakdown(
  lineItems: Array<{ taxSnapshot?: TaxSnapshot | Record<string, unknown> | null }>,
  expectedTaxTotal: number,
): AggregatedTaxBreakdownRow[] {
  const rows = aggregateTaxBreakdown(lineItems);
  if (rows.length === 0) return [];

  const roundCurrency = (amount: number) => Math.round(amount * 100) / 100;
  const displayedBreakdownTotal = roundCurrency(
    rows.reduce((sum, row) => sum + roundCurrency(row.amount), 0),
  );

  return displayedBreakdownTotal === roundCurrency(expectedTaxTotal) ? rows : [];
}

/**
 * Shape of a single row in the *pre-aggregated* `taxSnapshot` JSONB
 * column stored on `credit_memos` and `receipts` (PR #591 / #592 follow-
 * up). The BE writes one row per `(name, jurisdiction)` tuple at
 * write time, so the FE doesn't have to walk per-line snapshots here.
 */
export interface StoredAggregatedTaxBreakdownRow {
  name?: string | null;
  jurisdiction?: string | null;
  jurisdictionType?: string | null;
  rate: number;
  amount: number;
  registrationNumber?: string | null;
}

/**
 * Adapt the BE's persisted aggregate (credit memos, receipts) into the
 * `AggregatedTaxBreakdownRow` shape the existing renderers expect.
 *
 * The BE already collapsed entries by `(name, jurisdiction)`, so this
 * is a near-pass-through — we only re-derive `primary` / `secondary`
 * labels via `taxBreakdownLabel` to keep formatting consistent with
 * `aggregateTaxBreakdown` (e.g. "GST (Federal)" has no secondary).
 *
 * Returns `[]` for null/empty inputs so callers can `taxRows.length > 0`
 * gate on the rolled-up "Tax" fallback row identically to invoices.
 */
export function adaptStoredTaxBreakdown(
  rows: StoredAggregatedTaxBreakdownRow[] | null | undefined,
): AggregatedTaxBreakdownRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.map((row) => {
    const { primary, secondary } = taxBreakdownLabel({
      name: row.name ?? null,
      jurisdiction: row.jurisdiction ?? null,
    });
    return {
      primary,
      secondary,
      rate: row.rate,
      amount: row.amount,
      jurisdictionType: row.jurisdictionType ?? null,
      registrationNumber: row.registrationNumber ?? null,
    };
  });
}

// ─── Tax summary builder ──────────────────────────────────────────────────────

export function buildTaxSummary(
  lineItems: Array<{ taxSnapshot?: TaxSnapshot | null }>,
): TaxSummaryBreakdown[] {
  const totals = new Map<string, { rate: number; amount: number }>();

  for (const item of lineItems) {
    if (!item.taxSnapshot?.breakdown) continue;

    for (const breakdownItem of item.taxSnapshot.breakdown) {
      // Key on the v60 long-form name when present (e.g.
      // "US State Sales Tax — NJ" vs. "US County Sales Tax — BERGEN")
      // so the summary shows one row per tax code instead of collapsing
      // codes that share the legacy `jurisdiction` field.
      const { primary, secondary } = taxBreakdownLabel(breakdownItem);
      const key = secondary ? `${primary} — ${secondary}` : primary;
      const current = totals.get(key);
      if (current) {
        current.amount += breakdownItem.amount;
      } else {
        totals.set(key, {
          rate: breakdownItem.rate,
          amount: breakdownItem.amount,
        });
      }
    }
  }

  return Array.from(totals.entries()).map(([jurisdiction, data]) => ({
    jurisdiction,
    rate: data.rate,
    amount: data.amount,
  }));
}

/**
 * "Taxable Subtotal" — what the document's tax was charged ON: the net
 * (after-discount, pre-tax) amount of every line that carries tax.
 *
 * Printed above the tax rows so a customer can check the tax line themselves
 * (taxable subtotal x rate), and can see at a glance that a non-taxable line or
 * an exempt part was left out of it — which a bare "Sales Tax $33.00" under a
 * $1,000 subtotal does not say. Same row ServiceTitan prints.
 */
export function taxableSubtotalOf(
  lines: ReadonlyArray<{ lineTax?: unknown; lineTotal?: unknown; taxSnapshot?: unknown }>,
): number {
  const cents = lines.reduce((sum, line) => {
    if (!lineIsTaxed(line)) return sum;
    return sum + Math.round((Number(line.lineTotal ?? 0) - lineTaxOf(line)) * 100);
  }, 0);
  return cents / 100;
}

/**
 * Was this line part of what the tax was charged on?
 *
 * NOT "does it carry tax": tax is rounded once per document and the cents are
 * shared out, so a taxable line can be allocated $0.00 — a hundred $0.01 lines at
 * 10% owe $0.10 between them and only ten receive a cent. Asking for a positive
 * amount would print "Taxable Subtotal $0.10" over a $1.00 taxable base. A line
 * that a code APPLIED to has that code in its frozen breakdown, whatever it was
 * allocated; only a line with no snapshot at all falls back to its amount.
 */
function lineIsTaxed(line: { lineTax?: unknown; taxSnapshot?: unknown }): boolean {
  const breakdown = (line.taxSnapshot as { breakdown?: unknown[] } | null | undefined)?.breakdown;
  if (Array.isArray(breakdown)) return breakdown.length > 0;
  return lineTaxOf(line) > 0;
}

/**
 * A line's tax: its `lineTax` when the shape carries one, else the sum of its
 * frozen snapshot rows. Invoice lines reach the documents through mappers that
 * keep the snapshot but not `lineTax`; reading only the field printed
 * "Taxable Subtotal $0.00" above an $82.50 tax row.
 */
function lineTaxOf(line: { lineTax?: unknown; taxSnapshot?: unknown }): number {
  if (line.lineTax != null) {
    const tax = Number(line.lineTax);
    return Number.isFinite(tax) ? tax : 0;
  }
  const breakdown = (line.taxSnapshot as { breakdown?: Array<{ amount?: unknown }> } | null)
    ?.breakdown;
  if (!Array.isArray(breakdown)) return 0;
  return breakdown.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);
}
